import { fetchWithOptions } from 'Utility/fetch';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import { parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { IDefaultFetcherInterface, queryParamsBuilder } from './util';
import { TATTACHMENT } from '../types/TAttachment';

interface IAttachmentCreatePatient extends IDefaultFetcherInterface {
  name: 'createPatient';
  body: File;
  /** patientid, topicid, threadid, ?messageid? */
  queryParams: [string, string, string, string | undefined];
  contentType?: string;
}
interface IAttachmentCreateTopic extends IDefaultFetcherInterface {
  name: 'createTopic';
  body: File;
  /** topicid */
  queryParams: [string];
  contentType?: string;
}
interface IAttachmentDelete extends IDefaultFetcherInterface {
  name: 'delete';
  /** attachmentid OR patientid, attachmentid */
  queryParams: [string, string | undefined];
}
interface IAttachmentFetch extends IDefaultFetcherInterface {
  name: 'fetch';
  /** patientid, attachmentid */
  queryParams: [string, string];
}
interface IAttachmentFetchThumb extends IDefaultFetcherInterface {
  name: 'fetchThumb';
  /** patientid, attachmentid, thumbnailId */
  queryParams: [string, string, string];
}

type TAttachmentActions =
  | IAttachmentCreatePatient
  | IAttachmentCreateTopic
  | IAttachmentDelete
  | IAttachmentFetch
  | IAttachmentFetchThumb;

const basePath = '/api/messaging/v1';

const attachmentFetchConfig = {
  createPatient: {
    method: 'POST',
    path: basePath + '/attachment/message',
  },
  createTopic: {
    method: 'POST',
    path: basePath + '/attachment/topic',
  },
  delete: {
    method: 'DELETE',
    path: basePath + '/attachment',
  },
  fetch: {
    method: 'GET',
    path: basePath + '/attachment',
  },
  fetchThumb: {
    method: 'GET',
    path: basePath + '/thumbnail',
  },
};

type TDefaultReturn<N> = {
  returnType: N extends 'fetch' | 'fetchThumb'
    ? File
    : N extends 'createPatient' | 'createTopic'
      ? TATTACHMENT | false
      : boolean;
};

export const attachmentFetchBuilder = <T>(
  builderProps: TAttachmentActions,
): Promise<TDefaultReturn<T>['returnType'] | null> => {
  const actionName = builderProps.name;
  const path =
    attachmentFetchConfig[actionName].path +
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    `${builderProps.queryParams ? queryParamsBuilder(builderProps.queryParams) : ''}`;

  const bodyFile = builderProps.body ? { body: builderProps.body as File } : {};
  const contentType =
    (builderProps.name === 'createPatient' || builderProps.name === 'createTopic') && builderProps.contentType;

  if ((builderProps.name === 'createPatient' || builderProps.name === 'createTopic') && !contentType)
    return new Promise(() => false);

  return fetchWithOptions(
    path,
    { neurojwt: parseJWTFromCookie(), 'Content-Type': contentType ?? undefined },
    { method: attachmentFetchConfig[actionName].method, ...bodyFile },
  )
    .then(res => {
      if (res.status === 200) {
        if (actionName === 'fetch' || actionName === 'fetchThumb') {
          return res.blob();
        }
        // Return response body parsed, or true if it cannot be parsed
        return res.json().catch(() => true);
      } else throw new Error('unexpected status ' + res.status);
    })
    .catch((err: unknown) => {
      makeLog(
        Loggable.BugActionable,
        err,
        new ActionableErrorContext(null, `${attachmentFetchConfig[actionName].method} attachment`),
      );
      return null;
    });
};
